import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { BASE_URL } from "../utils/reportsHelpers";

/**
 * Fetches JSON row data for a report type from GET /reports/table — powers
 * both the enterprise DataTable and the chart data in ReportPreviewPage.
 * Pulls a generous page (500 rows, the server-side cap) so the client-side
 * DataTable can sort/paginate/virtualize without another round trip.
 */
const useReportTable = (currentUser, type, requestFilters) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [title, setTitle] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState(null);

  const fetchTable = useCallback(async () => {
    if (!currentUser || !type) return;
    setTableLoading(true);
    setTableError(null);
    try {
      const params = new URLSearchParams({
        type,
        infinityCode: currentUser,
        page: "1",
        pageSize: "500",
        filters: JSON.stringify(requestFilters || {}),
      });
      const res = await apiFetch(`${BASE_URL}/reports/table?${params.toString()}`, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
      setSummary(data.summary || {});
      setTitle(data.title || "");
      setTotalRows(data.totalRows || 0);
    } catch (err) {
      setTableError(err.message);
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  }, [currentUser, type, JSON.stringify(requestFilters)]);

  useEffect(() => { fetchTable(); }, [fetchTable]);

  return { rows, summary, title, totalRows, tableLoading, tableError, refetchTable: fetchTable };
};

export default useReportTable;
